import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { Router, RouterModule } from '@angular/router';
import { first, firstValueFrom, finalize, debounceTime, distinctUntilChanged } from 'rxjs';
import { Client, GeneralTabDto, GeneralTabListItemDto, IGeneralTabListItemDto, ReportType } from '../../../api/api-clientInformation';
import { MaterialModule } from '../../../material/material.module';
import { DropdownOptionValue, ToolbarComponent } from '../../../shared';
import moment from 'moment';
import { ApprovalFlowStateOptions, consequenceRankingOptions, FacilityEnumOptions, FDMStatusOptions, ReportTypeOptions, utcToLocal } from '@fdm/utils';
import { ApprovalFlowService, ButtonScriptsService, ReportFormPage, ReportFormStore } from '../../services';
import { GENERAL_TAB_LABELS } from '../../../utils/column-labels';
import { ApprovalStatusNamingPipe } from '../../pipes';
import { LoaderService } from '@fdm/services';
import { ToastrService } from 'ngx-toastr';
import { PolicyService } from '../../services/roles/policy.service';
import { CurrentUserService } from '../../../services/current-user.service';
import { SearchStorageService } from '../../../services/search-storage.service';
import { ColumnResizeDirective } from '../../../shared/directives';


const DEFAULT_PAGES = [25, 50, 75, 100];

enum FDMRecordStatus {
  ACTIVE_DEFICIENCY,
  ACTIVE_TASKDEFERRAL,
  ACTIVE_RELIABILITY,
  ACTIVE_INTEGRITY,
  ACTIVE_ANOMALY,
  ACTIVE_ALL,
  INACTIVE_ALL,
}

interface GeneralTabRecord extends IGeneralTabListItemDto {
  formattedReportType: string | null;
}

@Component({
  selector: 'app-my-approvals',
  templateUrl: './my-approvals.component.html',
  styleUrl: './my-approvals.component.scss',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    // Components
    ToolbarComponent,
    RouterModule,
    ApprovalStatusNamingPipe,
    ColumnResizeDirective
  ]
})

export class MyApprovalsComponent implements OnInit, AfterViewInit {
  // Expose Math to template
  Math = Math;
  private readonly COMPONENT_NAME = 'my-approvals';

  displayedColumns: string[] =
    ['uniqueId',
      'formattedReportType',
      'reportTitleEn',
      'status',
      'step',
      'discipline',
      'riptSeriatimRank',
      'facility',
      'abcPrioritization',
      'consequenceRanking',
      'waitingFor',
      'createdOn',
      'resolutionTargetDate',
      'closedOn',
      'initiator',
      'planApprovedDate'
    ];

  columnLabels = GENERAL_TAB_LABELS;
  dataSource: GeneralTabRecord[] = [];
  filteredData: GeneralTabRecord[] = [];
  paginatedData: GeneralTabRecord[] = [];
  selection = new SelectionModel<GeneralTabRecord>(true, []);
  @ViewChild(MatSort) sort!: MatSort;

  allRecords: GeneralTabListItemDto[] = [];
  control = new FormControl<string | null>(null);

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private apiClient = inject(Client);
  private router = inject(Router);
  private store = inject(ReportFormStore);
  private approvalFlow = inject(ApprovalFlowService);
  private currentUserService = inject(CurrentUserService);
  private searchStorage = inject(SearchStorageService);

  private _loaderService = inject(LoaderService);
  private _toasterService = inject(ToastrService);
  protected buttonScripts = inject(ButtonScriptsService);
  public canCreate$ = inject(PolicyService).canCreate$;

  pageIndex: number = 0;
  pageSize: number = 25;
  totalPageCount: number = 100;

  isProcessing = this._loaderService.processing$;
  isLoading = this._loaderService.loading$;

  statusFilter = signal<FDMRecordStatus>(FDMRecordStatus.ACTIVE_ALL);

  private currentSortActive: string = '';
  private currentSortDirection: 'asc' | 'desc' | '' = '';

  pageSizeOptions = DEFAULT_PAGES;
  isDisabled = () => this.selection.selected.length === 0;

  cloneReport() {
    const selectedReports = this.selection.selected;

    if (selectedReports.length === 0) {
      this._toasterService.error('No reports selected for cloning.');
      return;
    }

    this._loaderService.startProcessing();

    const cloneRequests = selectedReports.map(async report => {
      try {
        await firstValueFrom(this.apiClient.clone(report.id!).pipe(first()));
        this._toasterService.success(`Report "${report.reportTitleEn}" cloned successfully.`);
      } catch (err: any) {
        const errorMessage = err.errors?.[0] || err.message || 'Unknown error';
        this._toasterService.error(
          `Error cloning the report "${report.reportTitleEn}": ${errorMessage}`,
          'Cloning Failed',
          { timeOut: 12000 }
        );
      }
    });

    Promise.allSettled(cloneRequests)
      .finally(() => {
        this._loaderService.stopProcessing();
        this.selection.clear();
        this.loadReports();
      });
  }

  ngOnInit(): void {
    this.loadSearchFromStorage();
    this.loadReports();
    this.setupSearchFilter();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        // Save sort state
        this.currentSortActive = this.sort.active;
        this.currentSortDirection = this.sort.direction;

        // Reset to first page
        this.pageIndex = 0;

        // Fetch all data when sorting is activated
        const currentSearch = this.control.value || '';
        this.loadReports(currentSearch.trim());
      });
    }
  }

  private applySorting(): void {
    if (!this.currentSortActive || this.currentSortDirection === '') {
      this.updatePaginatedData();
      return;
    }

    const sortedData = [...this.filteredData].sort((a, b) => {
      const isAsc = this.currentSortDirection === 'asc';
      const aValue = (a as any)[this.currentSortActive];
      const bValue = (b as any)[this.currentSortActive];
      return this.compare(aValue, bValue, isAsc);
    });

    this.filteredData = sortedData;
    this.updatePaginatedData();
    this.cdr.markForCheck();
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    if (a == null && b == null) return 0;
    if (a == null) return isAsc ? 1 : -1;
    if (b == null) return isAsc ? -1 : 1;

    if (a instanceof Date && b instanceof Date) {
      return (a.getTime() - b.getTime()) * (isAsc ? 1 : -1);
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b) * (isAsc ? 1 : -1);
    }

    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private loadSearchFromStorage(): void {
    const savedSearch = this.searchStorage.getSearch(this.COMPONENT_NAME);
    if (savedSearch) {
      this.control.setValue(savedSearch, { emitEvent: false });
    }
  }

  private setupSearchFilter(): void {
    this.control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(searchText => {
      const searchValue = searchText || '';

      // Save to localStorage via service
      this.searchStorage.saveSearch(this.COMPONENT_NAME, searchValue);

      // Reset to first page when search changes
      this.pageIndex = 0;

      // Reload data with appropriate pagination based on search
      this.loadReports(searchValue.trim());
    });
  }

  private applySearchFilter(searchText: string): void {
    if (!searchText.trim()) {
      this.filteredData = [...this.dataSource];
    } else {
      const searchLower = searchText.toLowerCase();
      this.filteredData = this.dataSource.filter(record => {
        const uniqueId = (record.uniqueId || '').toLowerCase();
        const reportType = (record.formattedReportType || record.reportType || '').toLowerCase();
        const reportTitleEn = (record.reportTitleEn || '').toLowerCase();
        const status = (record.status || '').toLowerCase();
        const waitingFor = (record.waitingFor || '').toLowerCase();

        return uniqueId.includes(searchLower) ||
               reportType.includes(searchLower) ||
               reportTitleEn.includes(searchLower) ||
               status.includes(searchLower) ||
               waitingFor.includes(searchLower);
      });
    }

    this.applySorting();
  }

  private updatePaginatedData(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
  }

  getStatusLabel(step: string): string {
    const option = ApprovalFlowStateOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getStepLabel(step: string): string {
    const option = FDMStatusOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getReportLabel(step: string): string {
    const option = ReportTypeOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getConsequenceRankingLabel(step: string): string {
    const option = consequenceRankingOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }
  getFacilityLabel(step: string): string {
    const option = FacilityEnumOptions.find(opt => opt.value === step);
    return option ? option.label : step;
  }

  loadReports(searchText: string = ''): void {
    const currentUserEmail = this.currentUserService.getCurrentEmail();

    this._loaderService.startLoading();

    // When searching OR sorting, fetch all records (use large page size); otherwise use normal pagination
    const isSearching = searchText.trim().length > 0;
    const isSorting = this.currentSortActive && this.currentSortDirection !== '';
    const needsAllData = isSearching || isSorting;

    const pageNumber = needsAllData ? 1 : this.pageIndex + 1;
    const pageSize = needsAllData ? 10000 : this.pageSize;

    this.apiClient.generalTabGET(pageNumber, pageSize, undefined, undefined, undefined, undefined, undefined, undefined, undefined)
      .pipe(
        first(),
        finalize(() => {
          this._loaderService.stopLoading();
          this.cdr.markForCheck();
        })
      )
      .subscribe(records => {
        let items = records.items || [];

        // Filter by waitingFor matching current user email
        if (currentUserEmail) {
          items = items.filter(report =>
            report.waitingFor?.toLowerCase() === currentUserEmail.toLowerCase()
          );
        }

        this.dataSource = items.map(report => {
          const closedOn = report.closedOn ? moment(report.closedOn).local().toDate() : undefined;
          const resolutionTargetDate = report.resolutionTargetDate
            ? moment(report.resolutionTargetDate).local().toDate()
            : undefined;
          const formattedReportType = ReportTypeOptions.find(item => item.value === report.reportType)?.label ?? null;
          const consequenceRanking = report.consequenceRanking === 'NotAssigned' ? 'Not Assigned' : report.consequenceRanking;
          const createdOn = utcToLocal(report.createdOn)?.toDate() ?? undefined;

          return {
            ...report,
            closedOn,
            createdOn,
            consequenceRanking,
            formattedReportType,
            resolutionTargetDate
          }
        });

        this.totalPageCount = this.dataSource.length;

        // Apply search filter if searching, otherwise handle sorting
        if (isSearching) {
          this.applySearchFilter(searchText);
        } else if (isSorting) {
          // When sorting without search, apply to all loaded data
          this.filteredData = [...this.dataSource];
          this.applySorting();
        } else {
          this.filteredData = [...this.dataSource];
          this.updatePaginatedData();
        }

        this.cdr.markForCheck();
      });
  }

  isDateColumn(column: string): boolean {
    return [
      'resolutionTargetDate',
      'closedOn',
      'createdOn',
      'planApprovedDate',
    ].includes(column);
  }
  getCombinedColumns(): string[] {
    return ['select', ...this.displayedColumns];
    // return [...this.displayedColumns];
  }
  getColumnLabel(column: string): string {
    return this.columnLabels[column] || column;
  }
  isLogoColumn(column: string): boolean {
    return column === 'initiator';
  }

  getInitials(email: string | undefined): string {
    if (!email) return '';
    return email.slice(0, 2).toUpperCase();
  }


  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.paginatedData.length;
    return numRows > 0 && numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.paginatedData.forEach(row => this.selection.select(row));
  }

  onAddReport() {
    this.router.navigate(['my-approvals/add']);
    this.store.restore();
    this.approvalFlow.reset();
  }

  changedPageSize(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    const currentSearch = this.control.value || '';
    this.loadReports(currentSearch.trim());
  }

  // Custom pagination methods
  onPageChange(page: number): void {
    this.pageIndex = page - 1; // Convert to 0-based index
    const currentSearch = this.control.value || '';
    const isSorting = this.currentSortActive && this.currentSortDirection !== '';

    if (currentSearch.trim() || isSorting) {
      // If searching or sorting, just update local pagination (all data already loaded)
      this.updatePaginatedData();
    } else {
      // If not searching/sorting, fetch new page from backend
      this.loadReports();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0; // Reset to first page when changing page size
    const currentSearch = this.control.value || '';
    const isSorting = this.currentSortActive && this.currentSortDirection !== '';

    if (currentSearch.trim() || isSorting) {
      // If searching or sorting, just update local pagination (all data already loaded)
      this.updatePaginatedData();
    } else {
      // If not searching/sorting, fetch new page from backend
      this.loadReports();
    }
  }

  get totalPages(): number {
    const total = this.getTotalCount();
    return total > 0 ? Math.ceil(total / this.pageSize) : 1;
  }

  get currentPage(): number {
    return this.pageIndex + 1;
  }

  getTotalCount(): number {
    const currentSearch = this.control.value || '';
    const isSorting = this.currentSortActive && this.currentSortDirection !== '';
    // When searching or sorting, show filtered count; otherwise show total
    return (currentSearch.trim() || isSorting) ? this.filteredData.length : this.totalPageCount;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 3) {
        // Near the beginning
        pages.push(2, 3, 4, -1, total);
      } else if (current >= total - 2) {
        // Near the end
        pages.push(-1, total - 3, total - 2, total - 1, total);
      } else {
        // In the middle
        pages.push(-1, current - 1, current, current + 1, -1, total);
      }
    }

    return pages;
  }

  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.onPageChange(1);
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.onPageChange(this.totalPages);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.currentPage + 1);
    }
  }
}
